import { useState, useCallback, useEffect, useOptimistic, startTransition } from 'react';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { ARTIFACT_TYPES, EVENT_TYPES, MESSAGE_ROLES, SESSION_TYPES } from '../constants';

const isSystemError = block => block.type === ARTIFACT_TYPES.ERROR && block.error_type === EVENT_TYPES.ERROR

const useChatSessions = ({ api, sessionType = SESSION_TYPES.CHAT, onSessionChange = () => {}, onSessionRemoved = () => {}, loadSessionsOnMount = true }) => {
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [loadingSessionId, setLoadingSessionId] = useState(null);
    const [deletingSessionId, setDeletingSessionId] = useState(null);
    const [sessionDeletedNotice, setSessionDeletedNotice] = useState(null);

    const [optimisticSessions, updateOptimisticSessions] = useOptimistic(
        sessions,
        (currentSessions, { sessionId, title }) =>
            currentSessions.map(session =>
                session.session_id === sessionId ? { ...session, title } : session
            )
    );

    const fetchSessions = useCallback(async () => {
        setIsLoadingSessions(true);
        try {
            const response = await interactiveServiceFetch(`${api}/sessions?session_type=${sessionType}`);

            if (!response.ok) {
                throw new Error('Failed to fetch sessions');
            }

            const data = await response.json();
            setSessions(data.sessions || []);
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [api, sessionType]);

    useEffect(() => {
        if (loadSessionsOnMount) fetchSessions();
    }, [fetchSessions, loadSessionsOnMount]);

    const loadSession = useCallback(
        async sessionId => {
            if (!sessionId) return;

            setLoadingSessionId(sessionId);
            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}`);

                if (response.status === 404) {
                    setCurrentSessionId(null);
                    onSessionChange(null, true);
                    onSessionRemoved(sessionId);
                    setSessions(prev => prev.filter(s => s.session_id !== sessionId));
                    setSessionDeletedNotice(prev => ({
                        sessionId,
                        nonce: (prev?.nonce ?? 0) + 1,
                    }));
                    return null;
                }

                if (!response.ok) {
                    throw new Error('Failed to load session');
                }

                const data = await response.json();

                // Convert session messages to chat message format
                const loadedMessages = data.messages.map((msg, index) => {
                    return {
                        id: msg.id || `${msg.role}-${sessionId}-${index}`,
                        role: msg.role,
                        content: msg.content,
                        createdAt: new Date(msg.created_at * 1000), // Convert Unix timestamp to Date
                        isThinking: false,
                        isStreaming: false,
                        steps: [],
                        isHistorical: true, // Flag to indicate this is from history
                    };
                });

                // Check if the last assistant message ended with a report_error block
                const lastAssistantMsg = loadedMessages.findLast(({ role }) => role === MESSAGE_ROLES.ASSISTANT);
                const sessionError = lastAssistantMsg?.content?.findLast?.(isSystemError)?.data;

                if (sessionError) {
                    const lastErrorIdx = lastAssistantMsg.content.findLastIndex(isSystemError);
                    lastAssistantMsg.content = lastAssistantMsg.content.filter((_, i) => i !== lastErrorIdx);
                }

                setCurrentSessionId(sessionId);
                onSessionChange(sessionId, false, loadedMessages, sessionError, data.active_run_id || null);

                return loadedMessages;
            } catch (err) {
                console.error('Failed to load session:', err);
                return null;
            } finally {
                setLoadingSessionId(null);
            }
        },
        [api, onSessionChange, onSessionRemoved]
    );

    const deleteSession = useCallback(
        async sessionId => {
            if (!sessionId) return false;

            setDeletingSessionId(sessionId);
            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    throw new Error('Failed to delete session');
                }

                // If deleted session was current, clear it instead of creating a new one
                if (sessionId === currentSessionId) {
                    setCurrentSessionId(null);
                    onSessionChange(null, true);
                }

                onSessionRemoved(sessionId);
                setSessions(prev => prev.filter(s => s.session_id !== sessionId));

                return true;
            } catch (err) {
                // Leave the row visible on error so the user can retry.
                console.error('Failed to delete session:', err);
                return false;
            } finally {
                setDeletingSessionId(null);
            }
        },
        [api, currentSessionId, onSessionChange, onSessionRemoved]
    );

    const updateSessionTitle = useCallback(
        async (sessionId, title) => {
            if (!sessionId || !title) return false;

            startTransition(() => {
                updateOptimisticSessions({ sessionId, title });
            });

            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}/title`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title }),
                });

                if (!response.ok) {
                    throw new Error('Failed to update session title');
                }

                setSessions(prevSessions =>
                    prevSessions.map(session =>
                        session.session_id === sessionId
                            ? { ...session, title }
                            : session
                    )
                );

                return true;
            } catch (err) {
                console.error('Failed to update session title:', err);
                return false;
            }
        },
        [api, updateOptimisticSessions]
    );

    const upsertSession = useCallback((sessionId, title) => {
        setSessions(prev => {
            const exists = prev.some(session => session.session_id === sessionId);
            if (exists)
                return prev.map(session => (session.session_id === sessionId ? { ...session, title } : session));

            return [{ session_id: sessionId, title, message_count: 0 }, ...prev];
        });
    }, []);

    return {
        currentSessionId,
        setCurrentSessionId,
        sessions: optimisticSessions,
        isLoadingSessions,
        loadingSessionId,
        deletingSessionId,
        sessionDeletedNotice,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    };
};

export default useChatSessions;
